import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import { MONTANA_CONSTANTS } from '@/utils/entities/city/montana/montanaConstants';
import { DiscoveryCallBooking } from '@/services/cross-entity/discovery';

export interface ProposalSection {
  id: string;
  title: string;
  content: string;
  priority: 'high' | 'medium' | 'low';
  estimatedCost?: number;
  implementationTime?: string;
  dependencies?: string[];
}

export interface ROICalculation {
  category: string;
  description: string;
  annualSavings: number;
  implementationCost: number;
  paybackPeriod: number; // months
  confidence: 'high' | 'medium' | 'low';
}

export interface ImplementationPhase {
  phase: number;
  title: string;
  duration: string;
  deliverables: string[];
  milestones: string[];
  cost: number;
  dependencies?: string[];
}

export interface CustomProposal {
  id: string;
  communityName: string;
  tier: 'silver' | 'gold' | 'custom';
  createdAt: string;

  // Executive Summary
  executiveSummary: {
    currentChallenges: string[];
    proposedSolution: string;
    keyBenefits: string[];
    totalInvestment: number;
    expectedROI: number;
    paybackPeriod: number;
  };

  // Community Analysis
  communityAnalysis: {
    profileSummary: string;
    financialHealth: string;
    technologyReadiness: string;
    stakeholderAlignment: string;
    riskFactors: string[];
  };

  // Recommended Solutions
  recommendedSolutions: ProposalSection[];

  // ROI Analysis
  roiAnalysis: {
    calculations: ROICalculation[];
    totalAnnualSavings: number;
    totalImplementationCost: number;
    netPresentValue: number;
    returnOnInvestment: number;
  };

  // Implementation Plan
  implementationPlan: {
    phases: ImplementationPhase[];
    timeline: string;
    totalDuration: string;
    riskMitigation: string[];
  };

  // Investment Details
  investmentDetails: {
    subscriptionCost: number;
    setupFees: number;
    trainingCosts: number;
    customizationCosts: number;
    totalFirstYear: number;
    ongoingAnnual: number;
    addOns?: Array<{ id: string; name: string; pricingModel: string; priceLabel: string }>;
  };

  // Next Steps
  nextSteps: {
    immediateActions: string[];
    decisionTimeline: string;
    supportOffered: string[];
    contactInformation: {
      primaryContact: string;
      email: string;
      phone: string;
      calendlyLink?: string;
    };
  };
}

class ProposalGenerationService {
  generateCustomProposal(
    assessmentData: CommunityAssessmentData,
    discoveryCallData?: DiscoveryCallBooking,
  ): CustomProposal {
    const tier = assessmentData.selectedTier;
    const treasuryAmount = assessmentData.financialPosition?.treasuryAmount || 0;
    const totalUnits = parseInt(assessmentData.totalUnits);
    const monthlyFees = assessmentData.financialPosition?.monthlyHOAFees || 0;
    const annualRevenue = monthlyFees * totalUnits * 12;

    // Generate ROI calculations
    const roiCalculations = this.calculateROI(assessmentData);
    const totalAnnualSavings = roiCalculations.reduce((sum, calc) => sum + calc.annualSavings, 0);
    const totalImplementationCost = this.calculateImplementationCost(tier, assessmentData);

    // Calculate financial metrics
    const returnOnInvestment =
      ((totalAnnualSavings - totalImplementationCost) / totalImplementationCost) * 100;
    const paybackPeriod = Math.ceil(totalImplementationCost / (totalAnnualSavings / 12));
    const netPresentValue = this.calculateNPV(totalAnnualSavings, totalImplementationCost, 5, 0.08);

    const addOnLines = this.buildAddOnLines(assessmentData);

    const proposal: CustomProposal = {
      id: `proposal-${Date.now()}`,
      communityName: assessmentData.communityName,
      tier,
      createdAt: new Date().toISOString(),

      executiveSummary: {
        currentChallenges: assessmentData.biggestChallenges || [],
        proposedSolution: this.generateProposedSolution(tier, assessmentData),
        keyBenefits: this.generateKeyBenefits(assessmentData),
        totalInvestment: totalImplementationCost,
        expectedROI: Math.round(returnOnInvestment),
        paybackPeriod,
      },

      communityAnalysis: {
        profileSummary: this.generateProfileSummary(assessmentData),
        financialHealth: this.analyzeFinancialHealth(assessmentData),
        technologyReadiness: this.analyzeTechnologyReadiness(assessmentData),
        stakeholderAlignment: this.analyzeStakeholderAlignment(assessmentData),
        riskFactors: this.identifyRiskFactors(assessmentData),
      },

      recommendedSolutions: this.generateRecommendedSolutions(tier, assessmentData),

      roiAnalysis: {
        calculations: roiCalculations,
        totalAnnualSavings,
        totalImplementationCost,
        netPresentValue,
        returnOnInvestment,
      },

      implementationPlan: this.generateImplementationPlan(tier, assessmentData),

      investmentDetails: this.generateInvestmentDetails(tier, assessmentData, addOnLines),

      nextSteps: this.generateNextSteps(assessmentData, discoveryCallData),
    };

    return proposal;
  }

  private calculateROI(assessmentData: CommunityAssessmentData): ROICalculation[] {
    const calculations: ROICalculation[] = [];
    const totalUnits = parseInt(assessmentData.totalUnits);
    const monthlyFees = assessmentData.financialPosition?.monthlyHOAFees || 0;
    const annualRevenue = monthlyFees * totalUnits * 12;

    // Administrative Efficiency Savings
    if (assessmentData.primaryGoals?.includes('Reduce administrative burden')) {
      calculations.push({
        category: 'Administrative Efficiency',
        description: 'Reduced board member time spent on routine tasks',
        annualSavings: Math.min(annualRevenue * 0.08, 25000),
        implementationCost: 5000,
        paybackPeriod: 3,
        confidence: 'high',
      });
    }

    // Communication Cost Savings
    if (assessmentData.communicationIssues && assessmentData.communicationIssues.length > 0) {
      calculations.push({
        category: 'Communication Costs',
        description: 'Reduced mailing, printing, and meeting costs',
        annualSavings: Math.min(totalUnits * 50, 15000),
        implementationCost: 2000,
        paybackPeriod: 2,
        confidence: 'high',
      });
    }

    // Financial Management Efficiency
    if (assessmentData.financialConcerns && assessmentData.financialConcerns.length > 0) {
      calculations.push({
        category: 'Financial Management',
        description: 'Improved treasury management and investment returns',
        annualSavings: (assessmentData.financialPosition?.treasuryAmount || 0) * 0.03,
        implementationCost: 3000,
        paybackPeriod: 4,
        confidence: 'medium',
      });
    }

    // Vendor Management Savings
    if (assessmentData.maintenanceProblems && assessmentData.maintenanceProblems.length > 0) {
      calculations.push({
        category: 'Vendor Management',
        description: 'Better vendor pricing through improved processes',
        annualSavings: Math.min(annualRevenue * 0.05, 20000),
        implementationCost: 4000,
        paybackPeriod: 3,
        confidence: 'medium',
      });
    }

    // Legal and Compliance Savings
    calculations.push({
      category: 'Legal & Compliance',
      description: 'Reduced legal fees through better documentation and processes',
      annualSavings: Math.min(totalUnits * 25, 10000),
      implementationCost: 1500,
      paybackPeriod: 2,
      confidence: 'high',
    });

    return calculations;
  }

  private calculateImplementationCost(
    tier: string,
    assessmentData: CommunityAssessmentData,
  ): number {
    const baseSubscription = this.getAnnualSubscriptionCost(tier, assessmentData);
    const setupFees = this.getSetupFees(tier, assessmentData);
    const trainingCosts = this.getTrainingCosts(tier, assessmentData);
    const customizationCosts = this.getCustomizationCosts(tier, assessmentData);

    return baseSubscription + setupFees + trainingCosts + customizationCosts;
  }

  private getAnnualSubscriptionCost(tier: string, assessmentData: CommunityAssessmentData): number {
    const totalUnits = parseInt(assessmentData.totalUnits);

    switch (tier) {
      case 'silver':
        return Math.min(totalUnits * 15, 8000); // $15/unit, max $8k
      case 'gold':
        return Math.min(totalUnits * 25, 15000); // $25/unit, max $15k
      case 'custom':
        return Math.min(totalUnits * 40, 25000); // $40/unit, max $25k
      default:
        return totalUnits * 15;
    }
  }

  private getSetupFees(tier: string, assessmentData?: CommunityAssessmentData): number {
    // Compute per-parcel setup based on total units (parcels); migration work is an add-on and excluded
    const totalUnits = assessmentData
      ? parseInt(assessmentData.totalUnits) || MONTANA_CONSTANTS.DEFAULT_PARCELS
      : MONTANA_CONSTANTS.DEFAULT_PARCELS;
    switch (tier) {
      case 'silver':
        return Math.round(totalUnits * MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL.SILVER);
      case 'gold':
        return Math.round(totalUnits * MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL.GOLD);
      case 'custom':
        // Quote-based; default to 0 to be specified during scoping
        return 0;
      default:
        return Math.round(totalUnits * MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL.SILVER);
    }
  }

  private getTrainingCosts(tier: string, assessmentData: CommunityAssessmentData): number {
    const stakeholderCount = assessmentData.keyStakeholders?.length || 3;
    const baseTraining = stakeholderCount * 500;

    switch (tier) {
      case 'silver':
        return baseTraining;
      case 'gold':
        return baseTraining * 1.5;
      case 'custom':
        return baseTraining * 2;
      default:
        return baseTraining;
    }
  }

  private getCustomizationCosts(tier: string, assessmentData: CommunityAssessmentData): number {
    if (tier !== 'custom') return 0;

    // Base customization cost
    let cost = 5000;

    // Add costs based on specific needs
    if (assessmentData.investmentInterests?.includes('Custom reporting')) {
      cost += 3000;
    }
    if (assessmentData.investmentInterests?.includes('Third-party integrations')) {
      cost += 5000;
    }
    if (assessmentData.investmentInterests?.includes('White-label options')) {
      cost += 7000;
    }

    return cost;
  }

  private calculateNPV(
    annualCashFlow: number,
    initialInvestment: number,
    years: number,
    discountRate: number,
  ): number {
    let npv = -initialInvestment;
    for (let year = 1; year <= years; year++) {
      npv += annualCashFlow / Math.pow(1 + discountRate, year);
    }
    return Math.round(npv);
  }

  private generateProposedSolution(tier: string, assessmentData: CommunityAssessmentData): string {
    const communityName = assessmentData.communityName;
    const tierName = tier.charAt(0).toUpperCase() + tier.slice(1);

    return `We propose implementing our ${tierName} tier community management platform for ${communityName}. This comprehensive solution addresses your key challenges while providing modern tools for governance, financial management, and resident engagement. The platform will streamline your operations, improve transparency, and create new opportunities for community growth and financial optimization.`;
  }

  private generateKeyBenefits(assessmentData: CommunityAssessmentData): string[] {
    const benefits = [];

    if (assessmentData.primaryGoals?.includes('Improve financial transparency')) {
      benefits.push('Real-time financial dashboards and automated reporting');
    }
    if (assessmentData.primaryGoals?.includes('Streamline board operations')) {
      benefits.push('Automated workflows reducing administrative burden by 75%');
    }
    if (assessmentData.primaryGoals?.includes('Enhance resident communication')) {
      benefits.push('Multi-channel communication platform with 90% engagement rates');
    }
    if (assessmentData.primaryGoals?.includes('Modernize voting processes')) {
      benefits.push('Cryptographic voting system ensuring security and transparency');
    }

    // Add universal benefits
    benefits.push('Dedicated implementation support and training');
    benefits.push('Ongoing customer success management');
    benefits.push('ROI tracking and optimization recommendations');

    return benefits.slice(0, 6); // Limit to top 6 benefits
  }

  private generateProfileSummary(assessmentData: CommunityAssessmentData): string {
    const totalUnits = assessmentData.totalUnits;
    const treasuryAmount = assessmentData.financialPosition?.treasuryAmount || 0;
    const location = `${assessmentData.location?.city}, ${assessmentData.location?.state}`;

    return `${assessmentData.communityName} is a ${totalUnits}-unit community located in ${location}. With a treasury of $${treasuryAmount.toLocaleString()}, the community demonstrates solid financial management. The assessment reveals opportunities for operational improvement and technology modernization to enhance resident satisfaction and operational efficiency.`;
  }

  private analyzeFinancialHealth(assessmentData: CommunityAssessmentData): string {
    const treasuryAmount = assessmentData.financialPosition?.treasuryAmount || 0;
    const reserveFunds = assessmentData.financialPosition?.reserveFunds || 0;
    const outstandingDebts = assessmentData.financialPosition?.outstandingDebts || 0;
    const totalUnits = parseInt(assessmentData.totalUnits);

    const treasuryPerUnit = treasuryAmount / totalUnits;
    const reservePerUnit = reserveFunds / totalUnits;

    if (treasuryPerUnit > 1000 && reservePerUnit > 500) {
      return 'Strong financial position with adequate reserves and treasury funds. Well-positioned for technology investment and growth initiatives.';
    } else if (treasuryPerUnit > 500) {
      return 'Stable financial position with room for optimization. Technology investment will improve efficiency and long-term financial health.';
    } else {
      return 'Opportunity to strengthen financial position through improved operational efficiency and cost management via technology solutions.';
    }
  }

  private analyzeTechnologyReadiness(assessmentData: CommunityAssessmentData): string {
    const comfort = assessmentData.technologyAssessment?.technologyComfort || 'medium';
    const currentSoftware = assessmentData.technologyAssessment?.currentSoftware?.length || 0;

    if (comfort === 'high' && currentSoftware > 3) {
      return 'High technology readiness with existing software experience. Excellent foundation for platform adoption and advanced feature utilization.';
    } else if (comfort === 'medium') {
      return 'Moderate technology readiness with good potential for growth. Comprehensive training will ensure successful adoption and maximize value.';
    } else {
      return 'Developing technology readiness with significant opportunity for improvement. Dedicated support and gradual implementation will ensure successful transition.';
    }
  }

  private analyzeStakeholderAlignment(assessmentData: CommunityAssessmentData): string {
    const stakeholderCount = assessmentData.keyStakeholders?.length || 0;
    const buyIn = assessmentData.implementationReadiness?.stakeholderBuyIn || 'unknown';

    if (buyIn === 'strong' && stakeholderCount > 3) {
      return 'Strong stakeholder alignment with engaged leadership team. Excellent foundation for successful implementation and adoption.';
    } else if (buyIn === 'moderate') {
      return 'Good stakeholder alignment with opportunity to build additional support through demonstration and early wins.';
    } else {
      return 'Opportunity to build stakeholder alignment through education, demonstration, and phased implementation approach.';
    }
  }

  private identifyRiskFactors(assessmentData: CommunityAssessmentData): string[] {
    const risks = [];

    if (assessmentData.technologyAssessment?.technologyComfort === 'low') {
      risks.push('Low technology comfort level - mitigated through comprehensive training');
    }

    if (assessmentData.implementationReadiness?.stakeholderBuyIn === 'weak') {
      risks.push('Limited stakeholder buy-in - addressed through stakeholder education program');
    }

    if (assessmentData.implementationReadiness?.resourceAvailability === 'limited') {
      risks.push('Limited implementation resources - managed through phased rollout approach');
    }

    if (assessmentData.implementationReadiness?.biggestConcerns?.includes('User adoption')) {
      risks.push('User adoption concerns - mitigated through change management support');
    }

    if (risks.length === 0) {
      risks.push('Low implementation risk with strong community foundation');
    }

    return risks;
  }

  private generateRecommendedSolutions(
    tier: string,
    assessmentData: CommunityAssessmentData,
  ): ProposalSection[] {
    const solutions: ProposalSection[] = [];

    // Core platform always included
    solutions.push({
      id: 'core-platform',
      title: 'Core Community Management Platform',
      content: `Comprehensive platform including financial management, communication tools, document management, and basic voting capabilities. Designed specifically for ${assessmentData.communityName}'s ${assessmentData.totalUnits} units.`,
      priority: 'high',
      estimatedCost: this.getAnnualSubscriptionCost(tier, assessmentData),
      implementationTime: 'Varies by scope',
    });

    // Add tier-specific solutions
    if (tier === 'gold' || tier === 'custom') {
      solutions.push({
        id: 'advanced-voting',
        title: 'Advanced Voting & Approval Workflows',
        content:
          'Cryptographic voting system with custom approval workflows, automated ballot distribution, and real-time results tracking.',
        priority: 'high',
        estimatedCost: 0, // Included in tier
        implementationTime: 'Included in deployment',
        dependencies: ['core-platform'],
      });

      solutions.push({
        id: 'financial-analytics',
        title: 'Advanced Financial Analytics',
        content:
          'Comprehensive financial reporting, budget variance analysis, and ROI tracking with automated insights.',
        priority: 'medium',
        estimatedCost: 0, // Included in tier
        implementationTime: 'Included in deployment',
        dependencies: ['core-platform'],
      });
    }

    if (tier === 'custom') {
      solutions.push({
        id: 'custom-integrations',
        title: 'Custom Integrations & White-Label Options',
        content:
          'Seamless integration with existing systems and white-label customization to match community branding.',
        priority: 'medium',
        estimatedCost: this.getCustomizationCosts(tier, assessmentData),
        implementationTime: '6-8 weeks',
        dependencies: ['core-platform'],
      });
    }

    // Add solutions based on specific challenges
    if (assessmentData.maintenanceProblems && assessmentData.maintenanceProblems.length > 0) {
      solutions.push({
        id: 'vendor-management',
        title: 'Vendor & Maintenance Management',
        content:
          'Comprehensive vendor portal, work order tracking, and automated maintenance scheduling.',
        priority: 'medium',
        estimatedCost: tier === 'silver' ? 2000 : 0,
        implementationTime: '3-4 weeks',
        dependencies: ['core-platform'],
      });
    }

    return solutions;
  }

  private generateImplementationPlan(
    tier: string,
    assessmentData: CommunityAssessmentData,
  ): {
    phases: ImplementationPhase[];
    timeline: string;
    totalDuration: string;
    riskMitigation: string[];
  } {
    const phases: ImplementationPhase[] = [
      {
        phase: 1,
        title: 'Foundation Setup',
        duration: '2 weeks',
        deliverables: [
          'Platform configuration and customization',
          'Data migration from existing systems (add-on, scoped separately)',
          'User account setup and permissions',
          'Initial training materials preparation',
        ],
        milestones: [
          'Platform ready for testing',
          'If purchased, data migration complete',
          'Administrative accounts configured',
        ],
        cost: this.getSetupFees(tier, assessmentData),
      },
      {
        phase: 2,
        title: 'Training & Onboarding',
        duration: '2-3 weeks',
        deliverables: [
          'Board member training sessions',
          'Administrative user training',
          'Resident communication and onboarding',
          'Documentation and user guides',
        ],
        milestones: [
          'All administrators trained',
          'Residents informed and onboarded',
          'Support processes established',
        ],
        cost: this.getTrainingCosts(tier, assessmentData),
        dependencies: ['Phase 1 completion'],
      },
      {
        phase: 3,
        title: 'Go-Live & Optimization',
        duration: '2-4 weeks',
        deliverables: [
          'Full platform activation',
          'Performance monitoring and optimization',
          'User feedback collection and implementation',
          'Success metrics tracking',
        ],
        milestones: [
          'Platform fully operational',
          'User adoption targets met',
          'Performance benchmarks achieved',
        ],
        cost: 0,
      },
    ];

    if (tier === 'custom') {
      phases.splice(1, 0, {
        phase: 2,
        title: 'Custom Development',
        duration: '3-4 weeks',
        deliverables: [
          'Custom integrations development',
          'White-label customization',
          'Advanced feature configuration',
          'Custom reporting setup',
        ],
        milestones: [
          'Custom features developed',
          'Integration testing complete',
          'Customization approved',
        ],
        cost: this.getCustomizationCosts(tier, assessmentData),
        dependencies: ['Phase 1 completion'],
      });
    }

    const totalWeeks = phases.reduce((sum, phase) => {
      const weeks = parseInt(phase.duration.split('-')[0]) || parseInt(phase.duration);
      return sum + weeks;
    }, 0);

    return {
      phases,
      timeline: 'Implementation begins within 1 week of contract signing',
      totalDuration: `${totalWeeks}-${totalWeeks + 4} weeks`,
      riskMitigation: [
        'Dedicated project manager assigned',
        'Weekly progress reviews and adjustments',
        'Parallel training and setup activities',
        'Contingency planning for technical issues',
        'User adoption monitoring and support',
      ],
    };
  }

  private generateInvestmentDetails(
    tier: string,
    assessmentData: CommunityAssessmentData,
    addOns?: Array<{ id: string; name: string; pricingModel: string; priceLabel: string }>,
  ): {
    subscriptionCost: number;
    setupFees: number;
    trainingCosts: number;
    customizationCosts: number;
    totalFirstYear: number;
    ongoingAnnual: number;
    addOns?: Array<{ id: string; name: string; pricingModel: string; priceLabel: string }>;
  } {
    const subscriptionCost = this.getAnnualSubscriptionCost(tier, assessmentData);
    const setupFees = this.getSetupFees(tier, assessmentData);
    const trainingCosts = this.getTrainingCosts(tier, assessmentData);
    const customizationCosts = this.getCustomizationCosts(tier, assessmentData);

    return {
      subscriptionCost,
      setupFees,
      trainingCosts,
      customizationCosts,
      totalFirstYear: subscriptionCost + setupFees + trainingCosts + customizationCosts,
      ongoingAnnual: subscriptionCost,
      addOns,
    };
  }

  private buildAddOnLines(
    assessmentData: CommunityAssessmentData,
  ): Array<{ id: string; name: string; pricingModel: string; priceLabel: string }> {
    const ids = assessmentData.selectedAddOns || [];
    if (!ids.length) return [];
    // In a full system we'd source from a global catalog; here we create readable labels
    return ids.map((id) => {
      // Simple name mapping from id to human label
      const name = id
        .split('-')
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ');
      // Default to quote-based label to avoid implying fixed cost
      const priceLabel = 'Quote-based';
      const pricingModel = 'quote';
      return { id, name, pricingModel, priceLabel };
    });
  }

  private generateNextSteps(
    assessmentData: CommunityAssessmentData,
    discoveryCallData?: DiscoveryCallBooking,
  ): {
    immediateActions: string[];
    decisionTimeline: string;
    supportOffered: string[];
    contactInformation: {
      primaryContact: string;
      email: string;
      phone: string;
      calendlyLink?: string;
    };
  } {
    const timeline =
      assessmentData.implementationReadiness?.decisionMakingTimeline || 'within_month';

    return {
      immediateActions: [
        'Review proposal with key stakeholders',
        'Schedule board presentation (materials provided)',
        'Conduct reference calls with similar communities',
        'Review implementation timeline and resource requirements',
      ],
      decisionTimeline: this.getDecisionTimelineText(timeline),
      supportOffered: [
        'Board presentation materials and talking points',
        'Reference community introductions',
        'Detailed implementation project plan',
        'Risk assessment and mitigation strategies',
        'Financial model and ROI projections',
      ],
      contactInformation: {
        primaryContact: 'Community Solutions Specialist',
        email: 'solutions@example.com',
        phone: '(555) 123-4567',
        calendlyLink: 'https://calendly.com/community-solutions',
      },
    };
  }

  private getDecisionTimelineText(timeline: string): string {
    switch (timeline) {
      case 'immediate':
        return 'Decision expected within 1 week';
      case 'within_week':
        return 'Decision expected within 1 week';
      case 'within_month':
        return 'Decision expected within 1 month';
      case 'within_quarter':
        return 'Decision expected within 3 months';
      case 'annual_meeting':
        return 'Decision at next annual meeting';
      default:
        return 'Decision timeline to be determined';
    }
  }

  generateBoardPresentation(proposal: CustomProposal): {
    slides: Array<{
      title: string;
      content: string[];
      charts?: Array<{
        type: string;
        data: Record<string, unknown>;
        title: string;
      }>;
    }>;
  } {
    return {
      slides: [
        {
          title: 'Executive Summary',
          content: [
            `Investment: $${proposal.executiveSummary.totalInvestment.toLocaleString()}`,
            `Expected ROI: ${proposal.executiveSummary.expectedROI}%`,
            `Payback Period: ${proposal.executiveSummary.paybackPeriod} months`,
            `Annual Savings: $${proposal.roiAnalysis.totalAnnualSavings.toLocaleString()}`,
          ],
        },
        {
          title: 'Current Challenges',
          content: proposal.executiveSummary.currentChallenges,
        },
        {
          title: 'Proposed Solution',
          content: [proposal.executiveSummary.proposedSolution],
        },
        {
          title: 'Key Benefits',
          content: proposal.executiveSummary.keyBenefits,
        },
        {
          title: 'ROI Analysis',
          content: proposal.roiAnalysis.calculations.map(
            (calc) => `${calc.category}: $${calc.annualSavings.toLocaleString()} annual savings`,
          ),
        },
        {
          title: 'Implementation Timeline',
          content: proposal.implementationPlan.phases.map(
            (phase) => `Phase ${phase.phase}: ${phase.title} (${phase.duration})`,
          ),
        },
        {
          title: 'Investment Breakdown',
          content: [
            `Year 1 Total: $${proposal.investmentDetails.totalFirstYear.toLocaleString()}`,
            `Ongoing Annual: $${proposal.investmentDetails.ongoingAnnual.toLocaleString()}`,
            `Setup Fees: $${proposal.investmentDetails.setupFees.toLocaleString()}`,
            `Training: $${proposal.investmentDetails.trainingCosts.toLocaleString()}`,
          ],
        },
        {
          title: 'Next Steps',
          content: proposal.nextSteps.immediateActions,
        },
      ],
    };
  }

  saveProposal(proposal: CustomProposal): void {
    // In production, this would save to database
    localStorage.setItem(`proposal-${proposal.id}`, JSON.stringify(proposal));
    console.log(`Proposal saved: ${proposal.id}`);
  }

  getProposal(proposalId: string): CustomProposal | null {
    const stored = localStorage.getItem(`proposal-${proposalId}`);
    return stored ? JSON.parse(stored) : null;
  }

  getAllProposals(): CustomProposal[] {
    const proposals: CustomProposal[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('proposal-')) {
        const proposal = localStorage.getItem(key);
        if (proposal) {
          proposals.push(JSON.parse(proposal));
        }
      }
    }
    return proposals.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }
}

export const proposalGenerationService = new ProposalGenerationService();
